import React, { useEffect, useState } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Table, Button, Form, Row, Col } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { css } from "@emotion/react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { exportReportingList, getReportingList } from "../../data/applicationSlice"
import Loader from "../../components/Loader"
import { camelCaseToWords, downloadFile, formatDate } from "../../constant/utils"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const componentStyle = css`
    h3 {
        font-size: 16px;
    }

    button.run {
        margin-top: 23px;
    }

    h2 {
        color: #fff;
    }
`

const ReportingPage = () => {
    const dispatch = useDispatch()
    const { reportingData: reportingList, fetchStatus } = useSelector((state) => state.application)
    const [formData, setFormData] = useState({
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        showStatusAndRules: false,
        isSearched: false
    })

    const handleRunClick = () => {
        dispatch(getReportingList({ startDate: formData?.startDate, endDate: formData?.endDate }))
        if (!formData?.isSearched) {
            setFormData((pre) => ({
                ...pre,
                isSearched: true
            }))
        }
    }

    const handleExportClick = () => {
        dispatch(exportReportingList({ startDate: formData?.startDate, endDate: formData?.endDate })).then(
            ({ payload }) => {
                if (payload?.file) downloadFile(payload)
            }
        )
    }

    const update = (e, field) => {
        if (field === "startDate" || field === "endDate") {
            setFormData((pre) => ({
                ...pre,
                [field]: new Date(e).toISOString()
            }))
        } else if (!!e.target) {
            const { value, checked, type } = e.target
            setFormData((pre) => ({
                ...pre,
                [field]: type === "checkbox" ? checked : value
            }))
        }
    }

    return (
        <div
            css={componentStyle}
            className="page-container"
            style={{ marginTop: "80px" }}>
            <Loader show={fetchStatus === "loading"} />
            <h2>Reporting</h2>
            <div style={{ background: "white", padding: "20px", marginTop: "30px" }}>
                <h3>PCE Usage Report</h3>
                <Form>
                    <Row>
                        <Col>
                            <Form.Label>Start Date</Form.Label>
                            <DatePicker
                                selected={new Date(formData?.startDate)}
                                placeholderText="Select Date"
                                onChange={(date) => update(date, "startDate", "PCE")}
                                dateFormat="MM/dd/yyyy"
                            />
                        </Col>

                        <Col>
                            <Form.Group controlId="endDate">
                                <Form.Label>End Date</Form.Label>
                                <DatePicker
                                    selected={new Date(formData?.endDate)}
                                    placeholderText="Select Date"
                                    onChange={(date) => update(date, "endDate", "PCE")}
                                    dateFormat="MM/dd/yyyy"
                                    minDate={new Date(formData?.startDate)}
                                />
                            </Form.Group>
                        </Col>

                        <Col>
                            <Button
                                className="run"
                                size="sm"
                                variant="primary"
                                onClick={() => handleRunClick("PCE")}>
                                Run
                            </Button>
                        </Col>
                    </Row>
                </Form>
                {reportingList?.length === 0 && fetchStatus === "success" && formData?.isSearched && (
                    <p className="mt-4 pt-3 text-center h5">No Results</p>
                )}
                {reportingList?.length > 0 && (
                    <>
                        <div className="d-flex justify-content-end mb-1">
                            <div>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={handleExportClick}>
                                    <FontAwesomeIcon icon={byPrefixAndName.fal["file-export"]} /> Export
                                </Button>
                            </div>
                        </div>
                        <div className="d-flex justify-content-end mb-4">
                            <div className="ml-2">
                                <Form.Check
                                    type="checkbox"
                                    id="showStatusAndRules"
                                    name="showStatusAndRules"
                                    label="Show Status and Rules Applied"
                                    checked={formData?.showStatusAndRules}
                                    onChange={(e) => update(e, "showStatusAndRules")}
                                />
                            </div>
                        </div>
                        <Table
                            striped
                            bordered
                            hover
                            responsive>
                            <thead>
                                <tr>
                                    <th>ID Number</th>
                                    <th>First Named Insured</th>
                                    <th>Agency Name</th>
                                    <th>Agency Contact</th>
                                    <th>Date App Started</th>
                                    <th>Date App Submitted</th>
                                    <th>Created on Behalf</th>
                                    {formData?.showStatusAndRules && <th>Coverage Type Requested</th>}
                                    <th>Status</th>
                                    {formData?.showStatusAndRules && <th>Rule Applied</th>}
                                    {formData?.showStatusAndRules && <th>Quote Downloaded</th>}
                                    {formData?.showStatusAndRules && <th>Bind Request Date</th>}
                                </tr>
                            </thead>

                            <tbody>
                                {reportingList.map((data) => (
                                    <tr key={data.id}>
                                        <td>{data.friendlyId || ""}</td>
                                        <td>{data.firstNamedInsured || ""}</td>
                                        <td>{data.agencyName || ""}</td>
                                        <td>{data.agencyContact || ""}</td>
                                        <td>{formatDate(data.dateAppStarted) || ""}</td>
                                        <td>{formatDate(data.dateAppSubmitted) || "N/A"}</td>
                                        <td style={{ textAlign: "center" }}>
                                            {data?.createdOnBehalf ? (
                                                <span className="badge bg-success">Yes</span>
                                            ) : (
                                                <span className="badge bg-secondary">No</span>
                                            )}
                                        </td>
                                        {formData?.showStatusAndRules && <td>{data.coverageTypeRequested || "N/A"}</td>}
                                        <td>{camelCaseToWords(data?.status)}</td>
                                        {formData?.showStatusAndRules && (
                                            <td style={{ whiteSpace: "normal" }}>{data.rulesApplied || "N/A"}</td>
                                        )}
                                        {formData?.showStatusAndRules && <td>{data.quoteDownloaded || "N/A"}</td>}
                                        {formData?.showStatusAndRules && <td>{data.bindRequestDate || "N/A"}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </div>
        </div>
    )
}

export default ReportingPage
